import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarClock,
  ClipboardCheck,
  Layers3,
  RadioTower,
  Users,
} from "lucide-react";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { Progress } from "@/components/ui/progress";
import { cn, formatDateTime, timeAgo } from "@/lib/utils";

export function ProjectCard({ project, className }) {
  const action = project.nextAction || { label: "Open project", hint: "Review project activity.", tone: "draft" };
  const actionClasses = {
    draft: "border-[color:var(--accent-blue)]/40 bg-[color:var(--accent-blue)]/12 text-[color:var(--accent-cyan)]",
    review: "border-amber-200 bg-[color:var(--accent-amber)]/12 text-[color:var(--accent-amber)]",
    approved: "border-emerald-200 bg-[color:var(--accent-green)]/12 text-[color:var(--accent-green)]",
    scheduled: "border-[color:var(--accent-blue)]/40 bg-[color:var(--accent-blue)]/12 text-[color:var(--accent-cyan)]",
    published: "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-muted-foreground",
  };

  return (
    <Link
      to={`/projects/${project.id}`}
      data-testid={`project-card-${project.id}`}
      className={cn(
        "group relative block overflow-hidden rounded-xl border bg-card inner-highlight transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(18, 22, 20,0.07)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-cyan)]",
        className,
      )}
      style={{ borderColor: "var(--border-subtle)" }}
    >
      {project.cover_image ? (
        <div
          aria-hidden
          className="relative h-32 w-full overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(18, 22, 20,0.02) 0%, rgba(18, 22, 20,0.72) 100%), url(${project.cover_image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute right-2 top-2"><StatusBadge status={project.status} /></div>
        </div>
      ) : (
        <div className="h-2 w-full" style={{ background: "linear-gradient(90deg, rgba(18, 147, 79,0.35), rgba(143, 224, 74,0.35))" }} />
      )}

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              {project.pillars?.slice(0, 2).join(" · ") || "Project"}
            </p>
            <h3 className="mt-1 line-clamp-2 text-[15px] font-semibold tracking-[-0.005em] text-1">{project.title}</h3>
          </div>
          {!project.cover_image && <StatusBadge status={project.status} />}
        </div>

        <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{project.description}</p>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {project.platforms?.map((platform) => <PlatformBadge key={platform} platform={platform} compact />)}
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <MetricPill icon={Layers3} label="Assets" value={project.metrics?.assets ?? project.asset_count ?? 0} />
          <MetricPill icon={ClipboardCheck} label="Review" value={project.metrics?.needsReview ?? 0} />
          <MetricPill icon={RadioTower} label="Runs" value={project.metrics?.runs ?? 0} />
        </div>

        <div className="mt-4 rounded-lg border bg-[color:var(--surface-1)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold", actionClasses[action.tone] || actionClasses.draft)}>
                {action.label}
              </span>
              <p className="mt-2 line-clamp-2 text-[11.5px] leading-relaxed text-muted-foreground">{action.hint}</p>
            </div>
            <ArrowRight className="mt-1 h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-[color:var(--text-2)]" />
          </div>
        </div>

        {project.nextCalendarItem && (
          <div className="mt-3 flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-[11.5px] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
            <CalendarClock className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
            Next: {formatDateTime(project.nextCalendarItem.scheduled_at)}
          </div>
        )}

        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>{project.progressPercent ?? Math.round((project.progress || 0) * 100)}% complete</span>
            <span>{project.metrics?.deliverables ?? project.asset_count ?? 0} deliverables</span>
          </div>
          <Progress value={project.progressPercent ?? Math.round((project.progress || 0) * 100)} className="h-1.5" />
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span className="flex min-w-0 items-center gap-1"><Users className="h-3 w-3" /> {project.owner || "Adam"}</span>
          <span className="shrink-0">Updated {timeAgo(project.updated_at)}</span>
        </div>
      </div>
    </Link>
  );
}

function MetricPill({ icon: Icon, label, value }) {
  return (
    <div className="rounded-md border bg-card p-2" style={{ borderColor: "var(--border-subtle)" }}>
      <div className="flex items-center justify-between gap-1">
        <Icon className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
        <span className="font-mono text-sm font-semibold text-1">{value}</span>
      </div>
      <p className="mt-1 text-[10.5px] text-muted-foreground">{label}</p>
    </div>
  );
}
